import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";

const QrCodeImage = (props) => {
  const [index] = useState(props.keyid);
  const [image] = useState(props.imageUrl);
  const [Firstname] = useState(props.Firstname);

  const classes = useStyles();

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const imageToPrint = document.getElementById(`qr` + index).cloneNode(true);

    const printDocument = document.implementation.createHTMLDocument();
    const printBody = printDocument.body;

    // Inline print styles — no external CSS dependency
    const style = printDocument.createElement("style");
    style.textContent = `
      body { margin: 0; padding: 24px; background: #fff; display: flex; justify-content: center; }
      .printContainer { display: flex; flex-direction: column; align-items: center; gap: 10px; }
      .zoomed-image { width: 220px; height: 220px; object-fit: contain; }
      .printNameField { font-family: sans-serif; font-size: 16px; font-weight: 600; color: #111; text-align: center; }
    `;
    printDocument.head.appendChild(style);
    printBody.appendChild(imageToPrint);

    printWindow.document.open();
    printWindow.document.write(printDocument.documentElement.innerHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  if (!image) return null;

  return (
    <div className={classes.wrapper}>
      {/* QR card */}
      <div className={classes.card} id={`qr` + index}>
        {/* QR image — white bg for scannability */}
        <a href={image} download={index} className={classes.imageLink} title="Click to download">
          <div className={classes.imageBox}>
            <img className={classes.qrImage} src={image} alt="QR Code" />
            {/* Hover overlay */}
            <div className={classes.imageOverlay}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span className={classes.overlayText}>Download</span>
            </div>
          </div>
        </a>

        {/* Name */}
        {Firstname && (
          <span className={classes.name}>{Firstname}</span>
        )}
      </div>

      {/* Footer row: ticket ID + print button */}
      <div className={classes.footer}>
        <span className={classes.ticketId}>
          <span className={classes.ticketLabel}>Ticket ID</span>
          <span className={classes.ticketValue}>{index}</span>
        </span>

        <button className={classes.printBtn} onClick={handlePrint}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            style={{ marginRight: 6 }}>
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          Print
        </button>
      </div>
    </div>
  );
};

QrCodeImage.propTypes = {
  keyid: PropTypes.string,
  imageUrl: PropTypes.string,
  Firstname: PropTypes.string,
};

export default QrCodeImage;

const useStyles = makeStyles((theme) => ({
  "@keyframes qrPopIn": {
    from: { opacity: 0, transform: "scale(0.88) translateY(14px)" },
    to:   { opacity: 1, transform: "scale(1) translateY(0)" },
  },

  wrapper: {
    animation: "$qrPopIn 0.4s cubic-bezier(0.22,1,0.36,1) backwards",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "fit-content",
  },

  card: {
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    transition: "border-color 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.35)",
      boxShadow: "0 8px 32px rgba(232,27,35,0.15), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },

  imageLink: {
    display: "block",
    textDecoration: "none",
    borderRadius: "10px",
    overflow: "hidden",
    position: "relative",
  },

  imageBox: {
    position: "relative",
    background: "#fff",           // white pad so QR is scannable
    borderRadius: "10px",
    padding: "10px",
    lineHeight: 0,
  },

  qrImage: {
    width: "180px",
    height: "180px",
    display: "block",
    objectFit: "contain",
  },

  // Download overlay on hover
  imageOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(232,27,35,0.75)",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    opacity: 0,
    transition: "opacity 0.25s ease",
    "$imageLink:hover &": {
      opacity: 1,
    },
  },

  overlayText: {
    color: "#fff",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  name: {
    color: "#f0f0f5",
    fontWeight: 600,
    fontSize: "0.9rem",
    letterSpacing: "0.03em",
    textAlign: "center",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Footer row below the card
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "0 4px",
  },

  ticketId: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  ticketLabel: {
    color: "rgba(180,180,200,0.4)",
    fontSize: "0.72rem",
    fontWeight: 500,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },

  ticketValue: {
    fontFamily: "monospace",
    color: "rgba(210,210,225,0.65)",
    fontSize: "0.75rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "140px",
  },

  printBtn: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px",
    color: "rgba(210,210,225,0.75)",
    fontSize: "0.78rem",
    fontWeight: 600,
    padding: "6px 14px",
    cursor: "pointer",
    letterSpacing: "0.03em",
    transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "rgba(232,27,35,0.15)",
      borderColor: "rgba(232,27,35,0.45)",
      color: "#fff",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 14px rgba(232,27,35,0.25)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },
}));